import { useQueryClient, useQueryErrorResetBoundary } from '@tanstack/react-query'
import { Link, createFileRoute, useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { OfflineError } from '@/app/query-client'
import EmptyState from '@/components/ui/EmptyState'
import { stateActionClass } from '@/components/ui/empty-state-variants'
import TitleRow from '@/components/ui/TitleRow'
import { useSuspenseVerifiedCertificate } from '@/features/courses/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { parseCertificateCode } from '@/screens/courses/lib/params'
import VerifiedCertificateCard from '@/screens/courses/components/verify/VerifiedCertificateCard'
import VerifyCertificateSkeleton from '@/screens/courses/components/verify/VerifyCertificateSkeleton'
import { loadVerifyCertificate } from '@/screens/courses/VerifyCertificateScreen.loader'

const TITLE = 'Certificate Verification'

/**
 * The page's column under the public layout's brand bar. `<main>` lives here,
 * not in the layout, so the router's default error page (which brings its
 * own) never nests inside one. No ScreenLayout: its rail makes signed-in
 * requests, and this page is for anyone.
 */
function VerifyFrame({ children }: { children: ReactNode }) {
  return (
    // `@container` so SECTION_CARD's own `@max-sm` padding step (its 16px
    // phone gutter) applies here — this page has no ScreenLayout main column
    // to inherit one from (see the comment above).
    <main className="@container mx-auto flex w-full max-w-3xl flex-col gap-6 px-shell-x pb-16">
      <TitleRow title={TITLE} />
      {children}
    </main>
  )
}

function VerifyPending() {
  return (
    <VerifyFrame>
      <div aria-busy="true">
        <span role="status" className="sr-only">
          Checking this certificate…
        </span>
        <VerifyCertificateSkeleton />
      </div>
    </VerifyFrame>
  )
}

/**
 * This route's own `errorComponent` rather than the router's default
 * (`RouteErrorPage`): outside the signed-in shell that component fills the
 * WHOLE viewport (`min-h-dvh`), which is right for the auth and onboarding
 * screens it was written for — nothing else sits above them. Here `/_public`
 * already draws its own brand bar above the `<Outlet>`, so that same
 * full-viewport block would stack a second screen's worth of height under a
 * header already on screen. Wrapping in `VerifyFrame` instead keeps the
 * failed state in the page's own column, under the brand bar, exactly like
 * `VerifyPending` above it — the retry mechanics (offline detection, retrying
 * the failed query before re-running the loader, retrying again on its own
 * once the connection returns) mirror `RouteErrorPage`'s.
 */
function VerifyError({ error, reset: resetBoundary }: ErrorComponentProps) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const online = useOnline()
  const { reset } = useQueryErrorResetBoundary()
  const [retrying, setRetrying] = useState(false)

  useEffect(() => {
    reset()
  }, [reset])

  const offline = error instanceof OfflineError || !online

  const retry = () => {
    if (offline) return
    setRetrying(true)
    void (async () => {
      try {
        const failed = queryClient
          .getQueryCache()
          .findAll({ predicate: (query) => query.state.error === error })
        if (failed.length > 0) {
          await queryClient.refetchQueries({ predicate: (query) => failed.includes(query) })
          if (failed.some((query) => query.state.data === undefined)) return
        }
        await router.load()
        resetBoundary()
      } finally {
        setRetrying(false)
      }
    })()
  }

  // A transition from offline to online, not "is online" — see RouteErrorPage.
  const wasOnline = useRef(online)
  useEffect(() => {
    const cameBack = online && !wasOnline.current
    wasOnline.current = online
    if (cameBack) void router.load()
  }, [online, router])

  return (
    <VerifyFrame>
      <EmptyState
        kind={offline ? 'offline' : 'failed'}
        size="page"
        onRetry={retry}
        retrying={retrying}
      />
    </VerifyFrame>
  )
}

export const Route = createFileRoute('/_public/verify/$code')({
  loader: loadVerifyCertificate,
  pendingComponent: VerifyPending,
  errorComponent: VerifyError,
  component: VerifyCertificateRoute,
})

function CertificateNotFound({ code }: { code: string }) {
  return (
    <EmptyState
      kind="not-found"
      size="page"
      className="wrap-anywhere"
      title={`We couldn’t find a certificate with code ${code}`}
      description="Check the code and try again."
      action={
        // `/` sits inside the signed-in app layout, whose own guard sends an
        // anonymous visitor to /signin and carries them back here after —
        // one link that is correctly "home" or "sign in" for either reader.
        <Link to="/" className={stateActionClass('page')}>
          Go to MoneyMitra
        </Link>
      }
    />
  )
}

/** A code that cannot be one never reaches the suspense read (the loader skipped it too). */
function VerifyCertificateRoute() {
  const raw = Route.useParams().code
  const code = parseCertificateCode(raw)
  if (code === null) {
    return (
      <VerifyFrame>
        <CertificateNotFound code={raw} />
      </VerifyFrame>
    )
  }
  return <VerifyCertificateScreen key={code} code={code} />
}

/**
 * Public · a certificate checked against MoneyMitra's records — whoever a
 * holder sends this link to, signed in or not (this screen makes no
 * authenticated read; the loader skips one for the same reason).
 */
function VerifyCertificateScreen({ code }: { code: string }) {
  const { data: certificate } = useSuspenseVerifiedCertificate(code)
  return (
    <VerifyFrame>
      {certificate === null ? (
        <CertificateNotFound code={code} />
      ) : (
        <VerifiedCertificateCard certificate={certificate} />
      )}
    </VerifyFrame>
  )
}
